import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  SurveyQuestionData,
  SurveyQuestionCategoryData,
  SurveyPresetTemplateData,
  getSurveyQuestions,
  saveSurveyQuestion,
  updateSurveyQuestion,
  deleteSurveyQuestion,
  getSurveyQuestionCategories,
  getSurveyPresetTemplates,
  deleteField,
} from '@/lib/firebase';
import { useAuth } from '@/contexts/use-auth';
import { HelpCircle, Plus, Edit, Trash2, X, AlertTriangle, Search } from 'lucide-react';
import { useSearch } from 'wouter';

const formatModified = (ts: any): string => {
  if (!ts) return '-';
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    captureHandledException(error, "pulse-survey-questions.parse-date");
    return '-';
  }
};

const INPUT_TYPE_LABELS: Record<string, string> = {
  outOfFive: 'Out of Five',
  text: 'Text',
  yesNo: 'Yes / No',
};

interface FormData {
  questionText: string;
  additionalNotes: string;
  inputType: 'outOfFive' | 'text' | 'yesNo';
  categoryId: string;
  tags: string[];
  highPositive: boolean;
  includeNotes: boolean;
  drivesRecommendations: boolean;
  lowValueLabel: string;
  highValueLabel: string;
}

const EMPTY_FORM: FormData = {
  questionText: '',
  additionalNotes: '',
  inputType: 'outOfFive',
  categoryId: '',
  tags: [],
  highPositive: true,
  includeNotes: false,
  drivesRecommendations: false,
  lowValueLabel: '',
  highValueLabel: '',
};

function TemplateUsageBadge({ count, templateNames }: { count: number; templateNames: string[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (count === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800 hover:bg-violet-200 transition-colors cursor-pointer"
      >
        {count} template{count !== 1 ? 's' : ''}
      </button>
      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg py-1">
          <p className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
            Used in
          </p>
          {templateNames.map(name => (
            <p key={name} className="px-3 py-1.5 text-sm text-slate-700 truncate">
              {name}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PulseSurveyQuestionsPage() {
  const search = useSearch();
  const initialCategory = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('category') || 'all';
  }, []);

  const [questions, setQuestions] = useState<SurveyQuestionData[]>([]);
  const [categories, setCategories] = useState<SurveyQuestionCategoryData[]>([]);
  const [templates, setTemplates] = useState<SurveyPresetTemplateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SurveyQuestionData | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState(initialCategory);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [questionsData, categoriesData, templatesData] = await Promise.all([
        getSurveyQuestions(),
        getSurveyQuestionCategories(),
        getSurveyPresetTemplates(),
      ]);
      setQuestions(questionsData);
      setCategories(categoriesData.sort((a, b) => a.displayOrder - b.displayOrder));
      setTemplates(templatesData);
    } catch (error: any) {
      captureHandledException(error, "pulse-survey-questions.load");
      toast({ title: 'Failed to load data', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getTemplatesForQuestion = (questionId: string): SurveyPresetTemplateData[] =>
    templates.filter(t => (t.questions || []).some(q => q.questionId === questionId));

  const allExistingTags = Array.from(new Set(questions.flatMap(q => q.tags || []))).sort();

  const openAdd = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setTagInput('');
    setModalOpen(true);
  };

  const openEdit = (q: SurveyQuestionData) => {
    setEditing(q);
    setFormData({
      questionText: q.questionText,
      additionalNotes: q.additionalNotes || '',
      inputType: q.inputType,
      categoryId: q.categoryId,
      tags: [...(q.tags || [])],
      highPositive: q.highPositive,
      includeNotes: q.includeNotes,
      drivesRecommendations: q.drivesRecommendations,
      lowValueLabel: q.lowValueLabel || '',
      highValueLabel: q.highValueLabel || '',
    });
    setTagInput('');
    setModalOpen(true);
  };

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const filteredTagSuggestions = allExistingTags.filter(
    t => t.toLowerCase().includes(tagInput.toLowerCase()) && !formData.tags.includes(t)
  );

  const handleSave = async () => {
    if (!formData.questionText.trim()) {
      toast({ title: 'Question text required', description: 'Please enter the question text.', variant: 'destructive' });
      return;
    }
    if (!formData.categoryId) {
      toast({ title: 'Category required', description: 'Please select a category.', variant: 'destructive' });
      return;
    }
    if (!formData.inputType) {
      toast({ title: 'Input type required', description: 'Please select an input type.', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);

      const optionalText = (val: string) => val.trim() || undefined;

      if (editing) {
        const updatePayload: Record<string, any> = {
          questionText: formData.questionText.trim(),
          inputType: formData.inputType,
          categoryId: formData.categoryId,
          tags: formData.tags,
          highPositive: formData.highPositive,
          includeNotes: formData.includeNotes,
          drivesRecommendations: formData.drivesRecommendations,
          additionalNotes: optionalText(formData.additionalNotes) ?? deleteField(),
          lowValueLabel: optionalText(formData.lowValueLabel) ?? deleteField(),
          highValueLabel: optionalText(formData.highValueLabel) ?? deleteField(),
        };
        await updateSurveyQuestion(editing.id!, updatePayload);
        toast({ title: 'Question updated', description: 'Survey question has been updated.' });
      } else {
        const addPayload: Omit<SurveyQuestionData, 'id' | 'created' | 'modified'> = {
          questionText: formData.questionText.trim(),
          inputType: formData.inputType,
          categoryId: formData.categoryId,
          tags: formData.tags,
          highPositive: formData.highPositive,
          includeNotes: formData.includeNotes,
          drivesRecommendations: formData.drivesRecommendations,
        };
        if (optionalText(formData.additionalNotes)) addPayload.additionalNotes = formData.additionalNotes.trim();
        if (optionalText(formData.lowValueLabel)) addPayload.lowValueLabel = formData.lowValueLabel.trim();
        if (optionalText(formData.highValueLabel)) addPayload.highValueLabel = formData.highValueLabel.trim();
        await saveSurveyQuestion(addPayload);
        toast({ title: 'Question created', description: 'Survey question has been added.' });
      }
      setModalOpen(false);
      loadData();
    } catch (error: any) {
      captureHandledException(error, "pulse-survey-questions.save");
      toast({ title: 'Failed to save', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (q: SurveyQuestionData) => {
    const usedIn = getTemplatesForQuestion(q.id!);
    if (usedIn.length > 0) {
      const names = usedIn.map(t => `• ${t.name}`).join('\n');
      if (!confirm(
        `Warning: this question is used in ${usedIn.length} template${usedIn.length !== 1 ? 's' : ''}:\n\n${names}\n\nDeleting it will remove it from those templates. Continue?`
      )) return;
    } else {
      if (!confirm(`Delete this question? This cannot be undone.`)) return;
    }
    try {
      await deleteSurveyQuestion(q.id!);
      toast({ title: 'Question deleted', description: 'Survey question has been deleted.' });
      loadData();
    } catch (error: any) {
      captureHandledException(error, "pulse-survey-questions.delete");
      toast({ title: 'Failed to delete', description: error.message, variant: 'destructive' });
    }
  };

  const getCategoryName = (id: string) => categories.find(c => c.key === id)?.name || id;

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = !searchText.trim() ||
      q.questionText.toLowerCase().includes(searchText.trim().toLowerCase());
    const matchesCategory = filterCategoryId === 'all' || q.categoryId === filterCategoryId;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="flex-1 p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <HelpCircle className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Survey Questions</h1>
            <p className="text-slate-600">Manage pulse survey questions</p>
          </div>
        </div>
        <Button onClick={openAdd} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Add Question</span>
        </Button>
      </div>

      {!loading && questions.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              className="pl-9"
              placeholder="Search questions..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <Select value={filterCategoryId} onValueChange={setFilterCategoryId}>
            <SelectTrigger className="sm:w-56">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.key} value={cat.key}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!loading && questions.length > 0 && (searchText.trim() !== '' || filterCategoryId !== 'all') && (
        <p className="text-sm text-slate-500 mb-3">
          {filteredQuestions.length} of {questions.length} question{questions.length !== 1 ? 's' : ''}
        </p>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Loading questions...</p>
        </div>
      ) : questions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No questions yet</p>
            <Button onClick={openAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Question
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Question</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Category & Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Templates</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Modified</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredQuestions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                        No questions match your search or filter.
                      </td>
                    </tr>
                  ) : filteredQuestions.map((q) => {
                    const usedIn = getTemplatesForQuestion(q.id!);
                    return (
                      <tr key={q.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 max-w-xs">
                          <div className="font-medium text-slate-900">{q.questionText}</div>
                          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {INPUT_TYPE_LABELS[q.inputType] || q.inputType}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-600 mb-1">{getCategoryName(q.categoryId)}</div>
                          <div className="flex flex-wrap gap-1">
                            {(q.tags || []).slice(0, 4).map(tag => (
                              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                                {tag}
                              </span>
                            ))}
                            {(q.tags || []).length > 4 && (
                              <span className="text-xs text-slate-500">+{q.tags.length - 4}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <TemplateUsageBadge
                            count={usedIn.length}
                            templateNames={usedIn.map(t => t.name)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatModified(q.modified)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(q)} className="mr-2">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(q)}
                            className={usedIn.length > 0 ? 'text-amber-600 hover:text-amber-700' : 'text-red-600 hover:text-red-700'}
                          >
                            {usedIn.length > 0 ? <AlertTriangle className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Question' : 'Add Question'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Question Text */}
            <div>
              <Label htmlFor="q-text">Question Text *</Label>
              <Input
                id="q-text"
                value={formData.questionText}
                onChange={(e) => setFormData(prev => ({ ...prev, questionText: e.target.value }))}
                placeholder="Enter the survey question"
              />
            </div>

            {/* Additional Notes */}
            <div>
              <Label htmlFor="q-notes">Additional Notes</Label>
              <Textarea
                id="q-notes"
                value={formData.additionalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                placeholder="Optional guidance or context for this question"
                rows={3}
              />
            </div>

            {/* Input Type */}
            <div>
              <Label>Input Type *</Label>
              <Select
                value={formData.inputType}
                onValueChange={(v) => setFormData(prev => ({ ...prev, inputType: v as FormData['inputType'] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outOfFive">Out of Five</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="yesNo">Yes / No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div>
              <Label>Category *</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(v) => setFormData(prev => ({ ...prev, categoryId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.key} value={cat.key}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-700 text-sm"
                  >
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="relative">
                <Input
                  ref={tagInputRef}
                  value={tagInput}
                  onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => setShowTagSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
                  placeholder="Type a tag and press Enter or comma"
                />
                {showTagSuggestions && filteredTagSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-slate-200 rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                    {filteredTagSuggestions.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                        onMouseDown={() => addTag(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Press Enter or comma to add. Select from existing tags above.</p>
            </div>

            {formData.inputType === 'outOfFive' && (
              <>
                {/* Value Labels */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Value Labels</Label>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        lowValueLabel: 'Strongly Disagree',
                        highValueLabel: 'Strongly Agree',
                      }))}
                      className="text-xs text-primary hover:underline"
                    >
                      Use Strongly Disagree / Strongly Agree
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="q-low-label" className="text-xs text-slate-500 mb-1 block">Low Value Label</Label>
                      <Input
                        id="q-low-label"
                        value={formData.lowValueLabel}
                        onChange={(e) => setFormData(prev => ({ ...prev, lowValueLabel: e.target.value }))}
                        placeholder="e.g. Strongly Disagree"
                      />
                    </div>
                    <div>
                      <Label htmlFor="q-high-label" className="text-xs text-slate-500 mb-1 block">High Value Label</Label>
                      <Input
                        id="q-high-label"
                        value={formData.highValueLabel}
                        onChange={(e) => setFormData(prev => ({ ...prev, highValueLabel: e.target.value }))}
                        placeholder="e.g. Strongly Agree"
                      />
                    </div>
                  </div>
                </div>

                {/* High Positive */}
                <div>
                  <Label>High Value Interpretation *</Label>
                  <div className="flex gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, highPositive: true }))}
                      className={`flex-1 py-2 px-3 rounded border text-sm font-medium transition-colors ${
                        formData.highPositive
                          ? 'border-primary bg-primary text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      High value is positive
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, highPositive: false }))}
                      className={`flex-1 py-2 px-3 rounded border text-sm font-medium transition-colors ${
                        !formData.highPositive
                          ? 'border-primary bg-primary text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Low value is positive
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Switches */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">Include Notes</p>
                  <p className="text-xs text-slate-500">Allow respondents to add a note with their answer</p>
                </div>
                <Switch
                  checked={formData.includeNotes}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, includeNotes: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">Drives Recommendations</p>
                  <p className="text-xs text-slate-500">Use this question's result to generate recommendations</p>
                </div>
                <Switch
                  checked={formData.drivesRecommendations}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, drivesRecommendations: v }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Question'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
